import $ from 'jquery';
import bootbox from 'bootbox';
import { checkLogin, LoginResult } from 'JIX/utils/login.js';
import promisifyDialog from 'JIX/utils/promisifyDialog.js';
import showExplanation from './showExplanation.js';
import { confirmFollowCompany } from './CompanyFollow.js';
import { getAllowContactText } from 'JIX/myjob/api.js';
import { followCompany } from 'JIX/mycompany/api.js';
import { setContactConsent, getContactConsent } from 'JIX/user/api.js';
import { createMyjob, updateMyjob } from 'JIX/myjob/api.js';

const ELEMENT_SELECTOR = ".jobad-element-myjob-interest";

class LoginRequired extends Error {}

const Result = Object.freeze({
    SUCCESS: "success",
    ABORTED: "aborted",
});

export default class MyjobInterest {
    constructor(manager) {
        this.manager = manager;
        this.loggedInUser = false;

        $(document).on('click', `${ELEMENT_SELECTOR} a`, event => {
            event.preventDefault();
            const $link = $(event.currentTarget);
            if ($link.data('ignore')) {
                return;
            }
            $link.data('ignore', true);
            checkLogin()
                .then(loginResult => {
                    this.loggedInUser = loginResult === LoginResult.LOGGED_IN;
                    if (loginResult !== LoginResult.ABORTED) {
                        return this._handleClick($link)
                            .then(() => loginResult);
                    } else {
                        return loginResult;
                    }
                })
                .then(
                    loginResult => {
                        // If the user succesfully logged in, refresh the page
                        // so the login state is correctly reflected in the UI.
                        if (loginResult === LoginResult.LOGGED_IN) {
                            window.location.reload();
                        }
                    },
                    err => {
                        if (err instanceof LoginRequired) {
                            window.location = $link.attr('href');
                        } else {
                            const eventId = window.Sentry.captureException(err);
                            bootbox.alert(`Interessevisning fejlede: ${err.message} [Fejl ID: ${eventId}]`);
                        }
                    }
                )
                .finally(() => $link.data('ignore', false));
        });
    }

    replaceInstance($jobad, data) {
        const $el = $jobad.find(ELEMENT_SELECTOR);
        if (typeof data === "object") {
            $el.attr("data-state", data.state);
            $el.find('a').attr('href', data.url).text(data.label);
        } else {
            const $newEl = $(data);
            $el.replaceWith($newEl);
        }
    }

    /** Private methods **/

    _hasShownInterest($link) {
        return $link.parent().attr("data-state") === 'allowed';
    }
    _setShowInterest($link) {
        const tid = $link.attr('data-tid');

        if (!this._hasShownInterest($link)) {
            return createMyjob({ tid: tid, shown_interest: true });
        } else {
            return updateMyjob({ tid: tid }, { shown_interest: false });
        }
    }

    // User has logged in and we click the button
    _handleClick($link) {
        if (this._hasShownInterest($link)) {
            // The user has already shown interest for this job
            // Remove the display of interest.
            if (this.loggedInUser) {
                // The user was not logged in when clicking – do not
                // remove the show of interest is this case.
                return Result.SUCCESS;
            } else {
                return this._changeContact($link);
            }
        }

        // We just fetch cvissues in this endpoint
        return getContactConsent().then(infoResponse => {
            return this._addContact($link, infoResponse);
        });
    }

    // Asks for consent if applicable, otherwise show interest
    _addContact($link, infoResponse) {
        if (!infoResponse.remember || infoResponse.cvissues !== 'n') {
            return this._allowContactDialog($link, infoResponse.cvissues);
        } else {
            return this._changeContact($link);
        }
    }

    /**
     * Show confirmation dialog for showing interest.
     */
    _allowContactDialog($link, cvissues) {
        const $dialog = $('#jix_allow_contact_dialog');
        const promise = promisifyDialog($dialog, 'button.do_allow_contact')
            .then(({ accepted }) => {
                if (accepted) {
                    const remember = $('input[name=remember]', $dialog);

                    const consent = remember.is(':checked');
                    return setContactConsent(consent).then(() =>
                        this._changeContact($link, { fromModal: true })
                    );
                } else {
                    return Result.ABORTED;
                }
            });

        showExplanation($dialog, cvissues);
        getAllowContactText({ tid: $link.data('tid') })
            .then((interesttext) => {
                $dialog.find('#contact_dialog_company').text(interesttext);
                $dialog.modal(this._modalOptions());

            });
        return promise;

    }

    /**
     * Perform the display of interest.
     */
    _changeContact($link, options = {}) {
        const $jobad = this.manager.findClosestJobAd($link);

        return this._setShowInterest($link)
            .then(
                (changeResponse) => {
                    if (!this._hasShownInterest($link)) {
                        if (changeResponse.new) {
                            window.jixAnalytics('event', { category: 'jobannonce', action: 'click', label: 'vis-interesse-og-gem' });
                        } else {
                            window.jixAnalytics('event', { category: 'jobannonce', action: 'click', label: 'vis-interesse' });
                        }
                    }
                    this.manager.replaceElements($jobad, changeResponse.jobad_elements);
                    $link = null; // $link has been replaced

                    const is_req = Number(changeResponse.jobad_elements.companyid) === 0;
                    if (!is_req && options.fromModal) {
                        return this._confirmFollow(changeResponse.jobad_elements.companyid).then(() => Result.SUCCESS);
                    } else {
                        return Result.SUCCESS;
                    }
                }
            );
    }

    /**
     * Show dialog asking user if they want to follow the company.
     */
    _confirmFollow(cid) {
        return confirmFollowCompany(this._modalOptions(), 'dismiss')
            .then(({ accepted }) => {
                if (accepted) {
                    return this._followCompany(cid);
                }
            });
    }

    /**
     * Perform request for changing company follow status.
     */
    _followCompany(id) {
        return followCompany({ id }, { consentHandled: true })
            .then(() => {
                window.jixAnalytics('event', { category: 'jobannonce', action: 'click', label: 'vis-interesse-følg-virksomhed' });
                return Result.SUCCESS;
            });
    }

    _modalOptions() {
        // Make it harder for the user to accidentally close the modal if they
        // have just been logged in.
        return this.loggedInUser
            ? { show: true, backdrop: 'static', keyboard: false }
            : { show: true };
    }
}
